import React, { useState, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';
import './FaProofModal.css';

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE = 8 * 1024 * 1024;

// Two modes:
// - "send" (default): the Send to Client gate -- uploads the screenshot AND performs the send
//   in one request (POST .../send-to-client), since a candidate should never be marked sent
//   without proof attached.
// - "replace": admin/recruiting_admin swapping out an existing screenshot after the fact --
//   uploads only, does not touch interview_status or trigger a new client notification.
function FaProofModal({ candidate, mode = 'send', onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Set when the server's FA check fails: { reasons[], canOverride }
  const [checkFailure, setCheckFailure] = useState(null);
  const fileInputRef = useRef(null);

  const isReplace = mode === 'replace';

  const validateAndSetFile = (selected) => {
    if (!selected) return;
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setError('Please choose a JPG, PNG, or WEBP image.');
      return;
    }
    if (selected.size > MAX_SIZE) {
      setError('That image is too large -- please choose one under 8MB.');
      return;
    }
    setError('');
    setCheckFailure(null);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const handleFileInputChange = (e) => {
    validateAndSetFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndSetFile(e.dataTransfer.files?.[0]);
  };

  const clearFile = () => {
    setCheckFailure(null);
    setFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (override = false) => {
    if (!file) {
      setError('Please select a screenshot to upload.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('fa_proof', file);
      if (override) formData.append('override', 'true');
      const url = isReplace
        ? `${API_URL}/api/candidates/${candidate.id}/fa-proof/replace`
        : `${API_URL}/api/candidates/${candidate.id}/send-to-client`;
      const r = await axios.post(url, formData, { headers: { Authorization: `Bearer ${token}` } });
      onSuccess(r.data);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'FA_CHECK_FAILED') {
        setCheckFailure({ reasons: data.check?.reasons || [], canOverride: !!data.canOverride });
      }
      setError(data?.error || (isReplace ? 'Error replacing FA proof.' : 'Error sending candidate to client.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fapm-overlay" onClick={onClose}>
      <div className="fapm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fapm-header">
          <h2>{isReplace ? 'Replace FA Proof' : 'Upload FA Proof'}</h2>
          <button className="fapm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="fapm-body">
          <p className="fapm-instructions">
            {isReplace
              ? <>Upload a new screenshot for <strong>{candidate.candidate_name}</strong> to replace the one currently on file.</>
              : <>Before sending <strong>{candidate.candidate_name}</strong> to the client, upload a screenshot of their FA driver file showing the driver's <strong>name</strong> and the full <strong>Sections</strong> table. Every section must be green except <strong>Road Test</strong> and <strong>Training Cert</strong>, which the contractor completes.</>
            }
          </p>

          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {!file ? (
            <div
              className={`fapm-dropzone${dragOver ? ' drag-over' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="fapm-dropzone-icon">📎</div>
              <div className="fapm-dropzone-text">Drag & drop a screenshot here, or click to browse</div>
              <div className="fapm-dropzone-sub">JPG, PNG, or WEBP · Max 8MB</div>
            </div>
          ) : (
            <div className="fapm-preview">
              <img src={previewUrl} alt="FA proof preview" className="fapm-preview-img" />
              <div className="fapm-preview-info">
                <span className="fapm-preview-name">{file.name}</span>
                <button type="button" className="fapm-preview-remove" onClick={clearFile}>Remove</button>
              </div>
            </div>
          )}

          {error && <div className="fapm-error">{error}</div>}
          {checkFailure && checkFailure.reasons.length > 0 && (
            <ul className="fapm-check-list">
              {checkFailure.reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
        </div>

        <div className="fapm-footer">
          <button className="fapm-btn-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
          {checkFailure?.canOverride && !isReplace ? (
            <button className="fapm-btn-submit fapm-btn-override" onClick={() => handleSubmit(true)} disabled={submitting || !file}>
              {submitting ? 'Sending…' : 'Send anyway'}
            </button>
          ) : (
            <button className="fapm-btn-submit" onClick={() => handleSubmit(false)} disabled={submitting || !file}>
              {submitting ? (isReplace ? 'Uploading…' : 'Checking screenshot…') : (isReplace ? 'Replace' : 'Send to Client')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FaProofModal;
